import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GS1Document } from '../models/document.model';
import { LogoPartenairesVM } from '../models/logo.model';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  __URL_API__ = `${environment.URL_API}`;

  private _http = inject(HttpClient);
  private notificationService = inject(GS1NotificationService);
  private translate = inject(TranslateService);

  searchFactures(skip = 0, limit = 10): Observable<{ count: number; data: GS1Document[] }> {
    const params = new HttpParams().set('skip', String(skip)).set('limit', String(limit));

    return this._http
      .get<{
        count: number;
        data: GS1Document[];
      }>(this.__URL_API__ + 'api/Document/GetFactures', { params })
      .pipe(catchError((error) => this.handleError(error)));
  }

  getCGA(): Observable<GS1Document[]> {
    return this._http
      .get<GS1Document[]>(this.__URL_API__ + 'api/Document/GetCGA')
      .pipe(catchError((error) => this.handleError(error)));
  }
  getCGU(): Observable<GS1Document> {
    return this._http
      .get<GS1Document>(this.__URL_API__ + 'api/Document/GetCGU')
      .pipe(catchError((error) => this.handleError(error)));
  }
  getCGV(): Observable<GS1Document> {
    return this._http
      .get<GS1Document>(this.__URL_API__ + 'api/Document/GetCGV')
      .pipe(catchError((error) => this.handleError(error)));
  }
  getPolitiqueConfidentialite(): Observable<GS1Document> {
    return this._http
      .get<GS1Document>(this.__URL_API__ + 'api/Document/GetPolitiqueConfidentialite')
      .pipe(catchError((error) => this.handleError(error)));
  }

  getPackDocuments(): Observable<GS1Document[]> {
    return this._http
      .get<GS1Document[]>(this.__URL_API__ + 'api/Document/GetPackDocuments')
      .pipe(catchError((error) => this.handleError(error)));
  }

  searchMesDocuments(skip = 0, limit = 10): Observable<{ count: number; data: GS1Document[] }> {
    const params = new HttpParams().set('skip', skip).set('limit', String(limit));

    return this._http
      .get<{
        count: number;
        data: GS1Document[];
      }>(this.__URL_API__ + 'api/Document/GetMesDocuments', { params })
      .pipe(catchError((error) => this.handleError(error, 'MesDocuments.PROBLEME_RECUPERATION_DOCUMENTS')));
  }

  getLogosPartenaires(): Observable<LogoPartenairesVM> {
    return this._http
      .get<LogoPartenairesVM>(this.__URL_API__ + 'api/Document/GetLogosPartenaires')
      .pipe(catchError((error) => this.handleError(error)));
  }

  DownloadDocument(idDoc: string): Observable<any> {
    return this._http
      .get(this.__URL_API__ + 'api/Document/DownloadFile/' + idDoc, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response) => {
          const blob = response.body;
          if (blob != null) {
            const contentType = response.headers.get('Content-Type');
            const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
            if (contentType.includes('application/pdf')) {
              const a = document.createElement('a');
              a.href = url;
              a.download = idDoc + '.pdf';
              a.click();
            } else if (contentType.includes('text/html')) {
              window.open(url, '_blank');
            } else {
              throw new Error('Une erreur est survenue lors de la récupération du document demandé');
            }
          }
        }),
        catchError((err) =>
          this.handleError(err, 'Une erreur est survenue lors de la récupération du document demandé')
        )
      );
  }
  DownloadCGA(): Observable<Blob> {
    const fileName = "Conditions Générales d'Adhésion - GS1 France";
    return this._http
      .get(this.__URL_API__ + 'api/Document/GetPdfCGA', {
        responseType: 'blob' as const,
      })
      .pipe(
        tap((data) => {
          const file = new Blob([data as any], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
          const a = document.createElement('a');
          a.href = fileURL;
          a.target = '_blank';
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
        }),
        catchError((error) => this.handleError(error))
      );
  }

  DisplayDocument(idDoc: string): Observable<string> {
    return this._http
      .get(this.__URL_API__ + 'api/Document/DisplayFile/' + idDoc, {
        responseType: 'blob',
      })
      .pipe(
        map((blob) => window.URL.createObjectURL(blob)),
        catchError((error) => this.handleError(error))
      );
  }

  GetMembershipReport(): Observable<GS1Document> {
    return this._http
      .get<GS1Document>(this.__URL_API__ + 'api/Document/GetMembershipReport')
      .pipe(catchError((error) => this.handleError(error)));
  }

  private generateOneTimeAccessToken(idDoc: string): Observable<{ oneTimeToken: string }> {
    return this._http
      .get<{
        oneTimeToken: string;
      }>(this.__URL_API__ + 'api/Document/GenerateOneTimeAccessToken/' + idDoc)
      .pipe(catchError(() => throwError(() => ({ oneTimeToken: '' }))));
  }

  private handleError(error: any, message = 'GlobalErrors.default'): Observable<never> {
    this.notificationService.addNotification(this.translate.instant(message), 'error');
    return throwError(() => error.message || error);
  }
}
